/**
 * Chunk generator. `generateChunk` is a pure function of (seed, cx, cy, cz):
 * every block is computed from world coordinates alone, so the same chunk is
 * byte-identical no matter when, where, or in what order it is generated.
 * That property is what makes saves store only seed + edits, and what the
 * replay and worldgen tests guard.
 *
 * Pipeline per column, ported from the prototype and rescaled to the GDD's
 * vertical layout (surface ~y=0, sea level -2, floor -1024):
 * heightmap fbm → top/fill blocks → stratum stone by depth band → ore roll →
 * bedrock → 3D-noise caves → sea-level water → trees (clipped at chunk edges).
 */

import { BLOCK, type BlockId } from "../data/blocks";
import {
  BEDROCK_SALT,
  CAVES,
  ORES,
  SEA_LEVEL,
  STRATA,
  SURFACE,
  SURFACE_FILL,
  TREES,
  WORLD_CEILING,
  WORLD_FLOOR,
} from "../data/worldgen";
import { CHUNK_SIZE, blockIndex, createChunk, type Chunk } from "./chunk";
import { fbm2, fbm3, hash3 } from "./noise";

/**
 * Surface height of the column (x, z): two fbm fields remapped to [-1, 1) and
 * scaled, so terrain stays inside ±24 of y = 0. Exported because the spawn
 * point and (later) physics need the same ground truth as the generator.
 */
export function surfaceHeight(seed: number, x: number, z: number): number {
  const continent = fbm2(
    x * SURFACE.continent.frequency,
    z * SURFACE.continent.frequency,
    SURFACE.continent.octaves,
    seed + SURFACE.continent.salt,
  );
  const hills = fbm2(
    x * SURFACE.hills.frequency,
    z * SURFACE.hills.frequency,
    SURFACE.hills.octaves,
    seed + SURFACE.hills.salt,
  );
  return Math.floor(
    (continent - 0.5) * 2 * SURFACE.continent.amplitude +
      (hills - 0.5) * 2 * SURFACE.hills.amplitude,
  );
}

/** Base stone for a depth band, straight from the STRATA table. */
function stratumStone(y: number): BlockId {
  for (const band of STRATA) {
    if (y >= band.floorY && y < band.ceilingY) return band.stone;
  }
  return BLOCK.STONE;
}

/**
 * Ore roll, evaluated per vein-sized grid cell so a winning cell becomes a
 * small cubical vein. Pure in world coordinates, so veins crossing chunk
 * borders agree on both sides. `base` is returned when no ore fires.
 */
function rollOre(seed: number, wx: number, wy: number, wz: number, base: BlockId): BlockId {
  const depth = -wy;
  for (const ore of ORES) {
    if (depth < ore.minDepth || depth > ore.maxDepth) continue;
    const hit = hash3(
      seed + ore.salt,
      Math.floor(wx / ore.veinSize),
      Math.floor(wy / ore.veinSize),
      Math.floor(wz / ore.veinSize),
    );
    if (hit < ore.frequency) return ore.block;
  }
  return base;
}

/** True when the two cave channels agree this solid block is tunnel. */
function isCave(seed: number, wx: number, wy: number, wz: number): boolean {
  const a = fbm3(
    wx * CAVES.frequency,
    wy * CAVES.verticalFrequency,
    wz * CAVES.frequency,
    CAVES.octaves,
    seed + CAVES.saltA,
  );
  if (Math.abs(a - 0.5) >= CAVES.threshold) return false;
  const b = fbm3(
    wx * CAVES.frequency,
    wy * CAVES.verticalFrequency,
    wz * CAVES.frequency,
    CAVES.octaves,
    seed + CAVES.saltB,
  );
  return Math.abs(b - 0.5) < CAVES.threshold;
}

/**
 * Tree block write, clipped to the chunk. Trunks may overwrite leaves but
 * leaves may only claim air, so overlapping trees resolve to the same bytes
 * no matter which tree (or which neighbouring chunk) writes first.
 */
function putTreeBlock(chunk: Chunk, lx: number, ly: number, lz: number, id: BlockId): void {
  if (lx < 0 || lx >= CHUNK_SIZE || ly < 0 || ly >= CHUNK_SIZE || lz < 0 || lz >= CHUNK_SIZE) {
    return;
  }
  const i = blockIndex(lx, ly, lz);
  const current = chunk.blocks[i];
  if (current === BLOCK.AIR || (id === BLOCK.LOG && current === BLOCK.LEAVES)) {
    chunk.blocks[i] = id;
  }
}

/**
 * Trees are a property of the surface column, not of the chunk being filled:
 * a chunk checks every surface column whose tree could reach into it (the
 * 2-block leaves margin) and keeps only the blocks that land inside.
 */
function plantTrees(chunk: Chunk, seed: number, baseX: number, baseY: number, baseZ: number): void {
  const margin = TREES.leavesRadius;
  for (let tz = -margin; tz < CHUNK_SIZE + margin; tz++) {
    for (let tx = -margin; tx < CHUNK_SIZE + margin; tx++) {
      const wx = baseX + tx;
      const wz = baseZ + tz;
      const ground = surfaceHeight(seed, wx, wz);
      if (ground <= SURFACE_FILL.beachMaxY) continue;
      if (hash3(seed + TREES.saltChance, wx, 0, wz) >= TREES.chance) continue;

      const trunk =
        TREES.minTrunk +
        Math.floor(hash3(seed + TREES.saltTrunk, wx, 1, wz) * (TREES.maxTrunk - TREES.minTrunk + 1));
      const crownTop = ground + trunk + 2;
      // Skip trees whose whole span misses this chunk's y range.
      if (crownTop < baseY || ground + 1 > baseY + CHUNK_SIZE - 1) continue;

      for (let dy = 1; dy <= trunk; dy++) {
        putTreeBlock(chunk, tx, ground + dy - baseY, tz, BLOCK.LOG);
      }
      for (let ly = trunk - 2; ly <= trunk + 1; ly++) {
        const radius = ly >= trunk ? 1 : TREES.leavesRadius;
        const wy = ground + ly + 1;
        for (let ox = -radius; ox <= radius; ox++) {
          for (let oz = -radius; oz <= radius; oz++) {
            const corner = Math.abs(ox) === radius && Math.abs(oz) === radius;
            if (corner && hash3(seed + TREES.saltLeaves, wx + ox, wy, wz + oz) > 0.4) continue;
            if (ox === 0 && oz === 0 && ly < trunk) continue; // trunk holds the core
            putTreeBlock(chunk, tx + ox, wy - baseY, tz + oz, BLOCK.LEAVES);
          }
        }
      }
    }
  }
}

/** Generate one 32^3 chunk. Pure: same seed and coordinates, same bytes. */
export function generateChunk(seed: number, cx: number, cy: number, cz: number): Chunk {
  const chunk = createChunk(cx, cy, cz);
  const baseX = cx * CHUNK_SIZE;
  const baseY = cy * CHUNK_SIZE;
  const baseZ = cz * CHUNK_SIZE;

  // Whole-chunk fast paths: nothing above the ceiling, solid bedrock below the floor.
  if (baseY > WORLD_CEILING) return chunk;
  if (baseY + CHUNK_SIZE - 1 < WORLD_FLOOR) {
    chunk.blocks.fill(BLOCK.BEDROCK);
    return chunk;
  }

  for (let lz = 0; lz < CHUNK_SIZE; lz++) {
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      const wx = baseX + lx;
      const wz = baseZ + lz;
      const ground = surfaceHeight(seed, wx, wz);
      const beach = ground <= SURFACE_FILL.beachMaxY;

      for (let ly = 0; ly < CHUNK_SIZE; ly++) {
        const wy = baseY + ly;
        let block: BlockId = BLOCK.AIR;

        if (wy < WORLD_FLOOR || wy === WORLD_FLOOR) {
          block = BLOCK.BEDROCK;
        } else if (wy > WORLD_CEILING) {
          block = BLOCK.AIR;
        } else if (wy <= ground) {
          if (wy === ground) {
            block = beach ? BLOCK.SAND : BLOCK.GRASS;
          } else if (wy > ground - SURFACE_FILL.depth) {
            block = beach ? BLOCK.SAND : BLOCK.DIRT;
          } else {
            block = rollOre(seed, wx, wy, wz, stratumStone(wy));
            // Ragged bedrock just above the floor, thicker the deeper it gets.
            if (wy <= WORLD_FLOOR + 2 && hash3(seed + BEDROCK_SALT, wx, wy, wz) > (wy - WORLD_FLOOR) * 0.34) {
              block = BLOCK.BEDROCK;
            }
          }
          // Caves carve anything solid except the top blocks and the bedrock guard zone.
          if (
            block !== BLOCK.AIR &&
            block !== BLOCK.BEDROCK &&
            wy < ground - 1 &&
            wy > WORLD_FLOOR + CAVES.bedrockGuard &&
            isCave(seed, wx, wy, wz)
          ) {
            block = BLOCK.AIR;
          }
        } else if (wy <= SEA_LEVEL) {
          block = BLOCK.WATER;
        }

        if (block !== BLOCK.AIR) chunk.blocks[blockIndex(lx, ly, lz)] = block;
      }
    }
  }

  plantTrees(chunk, seed, baseX, baseY, baseZ);
  return chunk;
}
